import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import {
  User,
  Mail,
  CreditCard,
  Briefcase,
  Calendar,
  Clock,
  UserCheck,
  Key,
  Copy,
  Check,
  Edit3,
  Users,
  Heart,
  Send,
  Tag,
  MapPin,
  Landmark
} from 'lucide-react';
import { ClientDto, clientsApi } from '../../services/clientsService';
import { handleApiError } from '../../utils/errorHandler';
import { hexToRgba } from '../../utils/colorUtils';
import MedicalConditionsSection from './MedicalConditionsSection';
import CollapsibleSection from '../common/CollapsibleSection';

const Container = styled.div`
  padding: 20px;

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

/* ========== Header ========== */
const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--border-color);

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TitleIcon = styled.span`
  color: var(--primary-color);
  display: flex;
  align-items: center;
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 10px 20px;
  border-radius: var(--border-radius-md);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--primary-color);
    color: white;
  }

  @media (max-width: 767px) {
    width: 100%;
    justify-content: center;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 767px) {
    width: 100%;
    flex-direction: column;
  }
`;

/* ========== Section Structure ========== */
const Section = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--border-color);
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  color: var(--text-primary);
`;

const SectionIcon = styled.span<{ $color?: string }>`
  color: ${props => props.$color || 'var(--primary-color)'};
  display: flex;
  align-items: center;
`;

/* ========== Info Grid Layouts ========== */
const FourColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  row-gap: 12px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    row-gap: 0;
  }
`;

const ThreeColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  row-gap: 12px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    row-gap: 0;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: 12px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    row-gap: 0;
  }
`;

/* ========== Info Field ========== */
const InfoField = styled.div`
  padding: 10px 20px;
  border-left: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  gap: 6px;

  &:nth-child(3n+1) {
    border-left: none;
  }

  @media (max-width: 1024px) {
    &:nth-child(3n+1) {
      border-left: 1px solid var(--border-color);
    }
    &:nth-child(2n+1) {
      border-left: none;
    }
  }

  @media (max-width: 767px) {
    border-left: none;
    border-bottom: 1px solid var(--border-color);
    padding: 12px 0;

    &:last-child {
      border-bottom: none;
    }
  }
`;

/* ========== Info Field for 4-Column Grid ========== */
const FourColumnInfoField = styled.div`
  padding: 10px 20px;
  border-left: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  gap: 6px;

  &:nth-child(4n+1) {
    border-left: none;
  }

  @media (max-width: 1200px) {
    &:nth-child(4n+1) {
      border-left: 1px solid var(--border-color);
    }
    &:nth-child(2n+1) {
      border-left: none;
    }
  }

  @media (max-width: 767px) {
    border-left: none;
    border-bottom: 1px solid var(--border-color);
    padding: 12px 0;

    &:last-child {
      border-bottom: none;
    }
  }
`;

const FieldLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LabelIcon = styled.span<{ $color?: string }>`
  color: ${props => props.$color || 'var(--primary-color)'};
  display: flex;
  align-items: center;
`;

const FieldValue = styled.div`
  font-size: 17px;
  color: var(--text-primary);
  font-weight: 600;
  line-height: 1.4;
`;

const EmptyValue = styled.span`
  color: var(--text-secondary);
  font-weight: 400;
  font-style: italic;
`;

/* ========== Contacts Display ========== */
const ContactsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const ContactCard = styled.div<{ $isPrimary?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 10px;
  border-radius: 6px;
  background: ${props => props.$isPrimary ? 'rgba(22, 163, 74, 0.06)' : 'var(--background-color)'};
  border-right: 3px solid ${props => props.$isPrimary ? '#16a34a' : 'var(--border-color)'};
`;

const ContactPhoneLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const PrimaryStar = styled.span`
  color: #16a34a;
  font-size: 14px;
  line-height: 1;
`;

const ContactNumber = styled.span`
  font-size: 14px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: var(--text-primary);
`;

const ContactLabelTag = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(0, 142, 162, 0.1);
  color: #008EA2;
  font-weight: 500;
`;

const ContactEmailLine = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-secondary);

  & > svg {
    flex-shrink: 0;
  }
`;

/* ========== Status Badge ========== */
const StatusBadge = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-weight: 700;
  background: ${props => hexToRgba(props.color, 0.12)};
  color: ${props => props.color};
  border: 1px solid ${props => hexToRgba(props.color, 0.25)};
  width: fit-content;
`;

/* ========== Yes/No Indicator ========== */
const YesNoIndicator = styled.span<{ $isYes: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${props => props.$isYes ? '#16a34a' : 'var(--text-secondary)'};
  font-weight: ${props => props.$isYes ? '700' : '400'};
  font-size: 15px;
`;

/* ========== Password Field ========== */
const PasswordWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PasswordMask = styled.span`
  font-size: 16px;
  color: var(--text-secondary);
  letter-spacing: 2px;
`;

const CopyButton = styled.button<{ $success?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$success ? '#16a34a' : 'var(--background-secondary)'};
  color: ${props => props.$success ? 'white' : 'var(--text-secondary)'};

  &:hover:not(:disabled) {
    background: ${props => props.$success ? '#16a34a' : 'var(--primary-color)'};
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* ========== Clinical Questions Row ========== */
const QuestionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  padding: 10px 0;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const QuestionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
`;

const QuestionLabel = styled.span`
  color: var(--text-secondary);
  font-weight: 500;
`;

/* ========== Divider ========== */
const SectionDivider = styled.div`
  height: 1px;
  background: var(--border-color);
  margin: 20px 0;
`;

/* ========== Sub-Section (קומפקטי בתוך Section ראשי) ========== */
const SubSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px 0 10px 0;
  padding-bottom: 6px;
  border-bottom: 1px dashed var(--border-color);
`;

const SubSectionTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
`;

/* ========== תוויות מילולות לערכי enum ========== */
const BENEFICIARY_LABEL: Record<string, string> = {
  adult: 'מבוגר',
  child: 'ילד',
  other: 'אחר',
};

const SUBMITTER_SOURCE_LABEL: Record<string, string> = {
  father: 'אבא',
  mother: 'אמא',
  spouse: 'בן/בת זוג',
  other: 'אחר',
};

interface ClientInfoTabProps {
  client: ClientDto;
  formatDateOnly: (dateString?: string) => string;
  formatDateTime: (dateString?: string) => string;
  onEdit?: () => void;
  onManageSecretaries?: () => void;
}

const ClientInfoTab: React.FC<ClientInfoTabProps> = ({
  client,
  formatDateOnly,
  formatDateTime,
  onEdit,
  onManageSecretaries
}) => {
  const [copyingPassword, setCopyingPassword] = useState(false);

  // Calculate age from birthDate
  const calculateAge = (birthDateString: string): number => {
    const birthDate = new Date(birthDateString);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopyPassword = async () => {
    setCopyingPassword(true);
    setCopySuccess(false);

    try {
      const password = await clientsApi.getNationalInsurancePassword(client.id);
      if (!password) {
        toast.error('לא נמצאה סיסמה');
        return;
      }
      await navigator.clipboard.writeText(password);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בהעתקת הסיסמה'));
    } finally {
      setCopyingPassword(false);
    }
  };

  return (
    <Container>
      {/* Page Header */}
      <PageHeader>
        <PageTitle>
          <TitleIcon><User size={22} strokeWidth={2} /></TitleIcon>
          מידע אישי ורפואי
        </PageTitle>
        {(onEdit || onManageSecretaries) && (
          <HeaderActions>
            {onEdit && (
              <EditButton onClick={onEdit}>
                <Edit3 size={16} strokeWidth={2} />
                עריכת פרטים
              </EditButton>
            )}
            {onManageSecretaries && (
              <EditButton onClick={onManageSecretaries} type="button">
                <Users size={16} strokeWidth={2} />
                ניהול הרשאות מזכירות
              </EditButton>
            )}
          </HeaderActions>
        )}
      </PageHeader>

      {/* Personal Information */}
      <Section>
        <SectionHeader>
          <SectionIcon $color="var(--primary-color)"><User size={20} strokeWidth={2} /></SectionIcon>
          <SectionTitle>פרטים אישיים</SectionTitle>
        </SectionHeader>
        <FourColumnGrid>
          <FourColumnInfoField>
            <FieldLabel>
              <LabelIcon $color="var(--primary-color)"><User size={16} strokeWidth={2} /></LabelIcon>
              שם מלא
            </FieldLabel>
            <FieldValue>{client.fullName}</FieldValue>
          </FourColumnInfoField>

          <FourColumnInfoField>
            <FieldLabel>
              <LabelIcon $color="#6366f1"><CreditCard size={16} strokeWidth={2} /></LabelIcon>
              תעודת זהות
            </FieldLabel>
            <FieldValue>{client.idNumber}</FieldValue>
          </FourColumnInfoField>

          <FourColumnInfoField>
            <FieldLabel>
              <LabelIcon $color="#d97706"><Mail size={16} strokeWidth={2} /></LabelIcon>
              דוא״ל
            </FieldLabel>
            <FieldValue>
              {client.email || <EmptyValue>לא הוזן</EmptyValue>}
            </FieldValue>
          </FourColumnInfoField>

          <FourColumnInfoField>
            <FieldLabel>
              <LabelIcon $color="#0ea5e9"><Calendar size={16} strokeWidth={2} /></LabelIcon>
              תאריך לידה / גיל
            </FieldLabel>
            <FieldValue>
              {client.birthDate ? (
                <>
                  {formatDateOnly(client.birthDate)}
                  {' '}
                  <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                    ({calculateAge(client.birthDate)} שנים)
                  </span>
                </>
              ) : (
                <EmptyValue>לא הוזן</EmptyValue>
              )}
            </FieldValue>
          </FourColumnInfoField>
        </FourColumnGrid>
      </Section>

      <SectionDivider />

      {/* ============================================================
          אנשי קשר — מקטע מתכווץ
          ============================================================ */}
      <Section>
        <CollapsibleSection title="אנשי קשר" defaultOpen={true}>
          {client.contacts && client.contacts.length > 0 ? (
            <ContactsList>
              {client.contacts.map((contact, index) => (
                <ContactCard key={contact.id || index} $isPrimary={contact.isPrimary}>
                  <ContactPhoneLine>
                    {contact.isPrimary && <PrimaryStar title="איש קשר ראשי">★</PrimaryStar>}
                    <ContactNumber dir="ltr">{contact.number}</ContactNumber>
                    {contact.label && <ContactLabelTag>{contact.label}</ContactLabelTag>}
                  </ContactPhoneLine>
                  {contact.email && (
                    <ContactEmailLine>
                      <Mail size={12} strokeWidth={2} />
                      <span dir="ltr">{contact.email}</span>
                    </ContactEmailLine>
                  )}
                </ContactCard>
              ))}
            </ContactsList>
          ) : (
            client.phone || <EmptyValue>לא הוזן</EmptyValue>
          )}
        </CollapsibleSection>
      </Section>

      <SectionDivider />

      {/* ============================================================
          פרטים נוספים — מקטע מתכווץ עם סוג מוטב + הורים / בן זוג / מגיש
          ============================================================ */}
      <Section>
        <CollapsibleSection title="פרטים נוספים" defaultOpen={false}>
          {/* סוג מוטב */}
          {client.beneficiaryType && (
            <FourColumnGrid>
              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#0ea5e9"><Tag size={16} strokeWidth={2} /></LabelIcon>
                  סוג מוטב
                </FieldLabel>
                <FieldValue>
                  {BENEFICIARY_LABEL[client.beneficiaryType] || client.beneficiaryType}
                </FieldValue>
              </FourColumnInfoField>
            </FourColumnGrid>
          )}

        {/* ============================================================
            פרטי האב (מוצג רק אם יש נתונים)
            ============================================================ */}
        {(client.fatherName || client.fatherIdNumber || client.fatherAddress) && (
          <>
            <SubSectionHeader>
              <SectionIcon $color="#3b82f6"><User size={18} strokeWidth={2} /></SectionIcon>
              <SubSectionTitle>פרטי האב</SubSectionTitle>
            </SubSectionHeader>
            <FourColumnGrid>
              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#3b82f6"><User size={16} strokeWidth={2} /></LabelIcon>
                  שם
                </FieldLabel>
                <FieldValue>{client.fatherName || <EmptyValue>לא הוזן</EmptyValue>}</FieldValue>
              </FourColumnInfoField>

              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#6366f1"><CreditCard size={16} strokeWidth={2} /></LabelIcon>
                  תעודת זהות
                </FieldLabel>
                <FieldValue>
                  {client.fatherIdNumber ? (
                    <>
                      <span dir="ltr">{client.fatherIdNumber}</span>
                      {client.isFatherForeignResident && (
                        <span style={{ marginRight: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                          (תושב חו"ל)
                        </span>
                      )}
                    </>
                  ) : (
                    <EmptyValue>לא הוזן</EmptyValue>
                  )}
                </FieldValue>
              </FourColumnInfoField>

              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#14b8a6"><MapPin size={16} strokeWidth={2} /></LabelIcon>
                  כתובת
                </FieldLabel>
                <FieldValue>
                  {client.fatherAddress || client.fatherCity ? (
                    [client.fatherAddress, client.fatherCity].filter(Boolean).join(', ')
                  ) : (
                    <EmptyValue>לא הוזן</EmptyValue>
                  )}
                </FieldValue>
              </FourColumnInfoField>

              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#0ea5e9"><Calendar size={16} strokeWidth={2} /></LabelIcon>
                  תאריך לידה
                </FieldLabel>
                <FieldValue>
                  {client.fatherBirthDate
                    ? formatDateOnly(client.fatherBirthDate)
                    : <EmptyValue>לא הוזן</EmptyValue>}
                </FieldValue>
              </FourColumnInfoField>
            </FourColumnGrid>
          </>
        )}

        {/* ============================================================
            פרטי האם (מוצג רק אם יש נתונים)
            ============================================================ */}
        {(client.motherName || client.motherIdNumber || client.motherAddress) && (
          <>
            <SubSectionHeader>
              <SectionIcon $color="#ec4899"><User size={18} strokeWidth={2} /></SectionIcon>
              <SubSectionTitle>פרטי האם</SubSectionTitle>
            </SubSectionHeader>
            <FourColumnGrid>
              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#ec4899"><User size={16} strokeWidth={2} /></LabelIcon>
                  שם
                </FieldLabel>
                <FieldValue>{client.motherName || <EmptyValue>לא הוזן</EmptyValue>}</FieldValue>
              </FourColumnInfoField>

              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#6366f1"><CreditCard size={16} strokeWidth={2} /></LabelIcon>
                  תעודת זהות
                </FieldLabel>
                <FieldValue>
                  {client.motherIdNumber ? (
                    <>
                      <span dir="ltr">{client.motherIdNumber}</span>
                      {client.isMotherForeignResident && (
                        <span style={{ marginRight: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                          (תושב חו"ל)
                        </span>
                      )}
                    </>
                  ) : (
                    <EmptyValue>לא הוזן</EmptyValue>
                  )}
                </FieldValue>
              </FourColumnInfoField>

              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#14b8a6"><MapPin size={16} strokeWidth={2} /></LabelIcon>
                  כתובת
                </FieldLabel>
                <FieldValue>
                  {client.motherAddress || client.motherCity ? (
                    [client.motherAddress, client.motherCity].filter(Boolean).join(', ')
                  ) : (
                    <EmptyValue>לא הוזן</EmptyValue>
                  )}
                </FieldValue>
              </FourColumnInfoField>

              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#0ea5e9"><Calendar size={16} strokeWidth={2} /></LabelIcon>
                  תאריך לידה
                </FieldLabel>
                <FieldValue>
                  {client.motherBirthDate
                    ? formatDateOnly(client.motherBirthDate)
                    : <EmptyValue>לא הוזן</EmptyValue>}
                </FieldValue>
              </FourColumnInfoField>
            </FourColumnGrid>
          </>
        )}

        {/* ============================================================
            בן/בת זוג (מוצג רק אם יש נתונים)
            ============================================================ */}
        {(client.spouseName || client.spouseIdNumber || client.spouseAddress) && (
          <>
            <SubSectionHeader>
              <SectionIcon $color="#dc2626"><Heart size={18} strokeWidth={2} /></SectionIcon>
              <SubSectionTitle>פרטי בן/בת זוג</SubSectionTitle>
            </SubSectionHeader>
            <FourColumnGrid>
              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#dc2626"><User size={16} strokeWidth={2} /></LabelIcon>
                  שם
                </FieldLabel>
                <FieldValue>{client.spouseName || <EmptyValue>לא הוזן</EmptyValue>}</FieldValue>
              </FourColumnInfoField>

              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#6366f1"><CreditCard size={16} strokeWidth={2} /></LabelIcon>
                  תעודת זהות
                </FieldLabel>
                <FieldValue>
                  {client.spouseIdNumber ? (
                    <>
                      <span dir="ltr">{client.spouseIdNumber}</span>
                      {client.isSpouseForeignResident && (
                        <span style={{ marginRight: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                          (תושב חו"ל)
                        </span>
                      )}
                    </>
                  ) : (
                    <EmptyValue>לא הוזן</EmptyValue>
                  )}
                </FieldValue>
              </FourColumnInfoField>

              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#14b8a6"><MapPin size={16} strokeWidth={2} /></LabelIcon>
                  כתובת
                </FieldLabel>
                <FieldValue>
                  {client.spouseAddress || client.spouseCity ? (
                    [client.spouseAddress, client.spouseCity].filter(Boolean).join(', ')
                  ) : (
                    <EmptyValue>לא הוזן</EmptyValue>
                  )}
                </FieldValue>
              </FourColumnInfoField>

              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#0ea5e9"><Calendar size={16} strokeWidth={2} /></LabelIcon>
                  תאריך לידה
                </FieldLabel>
                <FieldValue>
                  {client.spouseBirthDate
                    ? formatDateOnly(client.spouseBirthDate)
                    : <EmptyValue>לא הוזן</EmptyValue>}
                </FieldValue>
              </FourColumnInfoField>
            </FourColumnGrid>
          </>
        )}

        {/* ============================================================
            מגיש הבקשה (מוצג רק אם יש נתונים)
            ============================================================ */}
        {(client.submitterName || client.submitterIdNumber) && (
          <>
            <SubSectionHeader>
              <SectionIcon $color="#16a34a"><Send size={18} strokeWidth={2} /></SectionIcon>
              <SubSectionTitle>מגיש הבקשה</SubSectionTitle>
            </SubSectionHeader>
            <FourColumnGrid>
              {client.submitterSourceType && (
                <FourColumnInfoField>
                  <FieldLabel>
                    <LabelIcon $color="#16a34a"><Users size={16} strokeWidth={2} /></LabelIcon>
                    מקור פרטי המגיש
                  </FieldLabel>
                  <FieldValue>
                    {SUBMITTER_SOURCE_LABEL[client.submitterSourceType] || client.submitterSourceType}
                  </FieldValue>
                </FourColumnInfoField>
              )}

              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#16a34a"><User size={16} strokeWidth={2} /></LabelIcon>
                  שם המגיש
                </FieldLabel>
                <FieldValue>{client.submitterName || <EmptyValue>לא הוזן</EmptyValue>}</FieldValue>
              </FourColumnInfoField>

              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#6366f1"><CreditCard size={16} strokeWidth={2} /></LabelIcon>
                  תעודת זהות
                </FieldLabel>
                <FieldValue>
                  {client.submitterIdNumber
                    ? <span dir="ltr">{client.submitterIdNumber}</span>
                    : <EmptyValue>לא הוזן</EmptyValue>}
                </FieldValue>
              </FourColumnInfoField>

              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#8b5cf6"><UserCheck size={16} strokeWidth={2} /></LabelIcon>
                  קרבת משפחה
                </FieldLabel>
                <FieldValue>
                  {client.submitterRelationship || <EmptyValue>לא הוזן</EmptyValue>}
                </FieldValue>
              </FourColumnInfoField>

              <FourColumnInfoField>
                <FieldLabel>
                  <LabelIcon $color="#14b8a6"><MapPin size={16} strokeWidth={2} /></LabelIcon>
                  כתובת
                </FieldLabel>
                <FieldValue>
                  {client.submitterAddress || client.submitterCity ? (
                    [client.submitterAddress, client.submitterCity].filter(Boolean).join(', ')
                  ) : (
                    <EmptyValue>לא הוזן</EmptyValue>
                  )}
                </FieldValue>
              </FourColumnInfoField>
            </FourColumnGrid>
          </>
        )}
        </CollapsibleSection>
      </Section>

      <SectionDivider />

      {/* ============================================================
          פרטי חשבון בנק להעברת קצבה — מקטע מתכווץ
          פתוח כברירת מחדל אם יש נתונים, אחרת מקופל
          ============================================================ */}
      <Section>
        <CollapsibleSection
          title="פרטי חשבון בנק להעברת קצבה"
          defaultOpen={!!(client.beneficiaryName || client.beneficiaryIdNumber || client.bankCode || client.branchNumber || client.accountNumber || client.payerName || client.payerIdNumber)}
        >
          <SubSectionHeader>
            <SectionIcon $color="var(--primary-color)"><User size={18} strokeWidth={2} /></SectionIcon>
            <SubSectionTitle>פרטי מקבל הקצבה</SubSectionTitle>
          </SubSectionHeader>
          <FourColumnGrid>
            <FourColumnInfoField>
              <FieldLabel>
                <LabelIcon $color="var(--primary-color)"><User size={16} strokeWidth={2} /></LabelIcon>
                שם מוטב
              </FieldLabel>
              <FieldValue>
                {client.beneficiaryName || <EmptyValue>לא הוזן</EmptyValue>}
              </FieldValue>
            </FourColumnInfoField>

            <FourColumnInfoField>
              <FieldLabel>
                <LabelIcon $color="#6366f1"><CreditCard size={16} strokeWidth={2} /></LabelIcon>
                ת.ז. מוטב
              </FieldLabel>
              <FieldValue>
                {client.beneficiaryIdNumber ? (
                  <span dir="ltr">{client.beneficiaryIdNumber}</span>
                ) : (
                  <EmptyValue>לא הוזן</EmptyValue>
                )}
              </FieldValue>
            </FourColumnInfoField>

            <FourColumnInfoField>
              <FieldLabel>
                <LabelIcon $color="#0ea5e9"><Landmark size={16} strokeWidth={2} /></LabelIcon>
                קוד בנק
              </FieldLabel>
              <FieldValue>
                {client.bankCode ? (
                  <span dir="ltr">{client.bankCode}</span>
                ) : (
                  <EmptyValue>לא הוזן</EmptyValue>
                )}
              </FieldValue>
            </FourColumnInfoField>

            <FourColumnInfoField>
              <FieldLabel>
                <LabelIcon $color="#14b8a6"><Landmark size={16} strokeWidth={2} /></LabelIcon>
                מספר סניף
              </FieldLabel>
              <FieldValue>
                {client.branchNumber ? (
                  <span dir="ltr">{client.branchNumber}</span>
                ) : (
                  <EmptyValue>לא הוזן</EmptyValue>
                )}
              </FieldValue>
            </FourColumnInfoField>

            <FourColumnInfoField>
              <FieldLabel>
                <LabelIcon $color="#16a34a"><CreditCard size={16} strokeWidth={2} /></LabelIcon>
                מספר חשבון
              </FieldLabel>
              <FieldValue>
                {client.accountNumber ? (
                  <span dir="ltr">{client.accountNumber}</span>
                ) : (
                  <EmptyValue>לא הוזן</EmptyValue>
                )}
              </FieldValue>
            </FourColumnInfoField>
          </FourColumnGrid>

          <SubSectionHeader>
            <SectionIcon $color="#6366f1"><User size={18} strokeWidth={2} /></SectionIcon>
            <SubSectionTitle>פרטי משלם הקצבה</SubSectionTitle>
          </SubSectionHeader>
          <FourColumnGrid>
            <FourColumnInfoField>
              <FieldLabel>
                <LabelIcon $color="var(--primary-color)"><User size={16} strokeWidth={2} /></LabelIcon>
                שם משלם הקצבה
              </FieldLabel>
              <FieldValue>
                {client.payerName || <EmptyValue>לא הוזן</EmptyValue>}
              </FieldValue>
            </FourColumnInfoField>

            <FourColumnInfoField>
              <FieldLabel>
                <LabelIcon $color="#6366f1"><CreditCard size={16} strokeWidth={2} /></LabelIcon>
                ת.ז. משלם הקצבה
              </FieldLabel>
              <FieldValue>
                {client.payerIdNumber ? (
                  <span dir="ltr">
                    {client.payerIdNumber}
                    {client.isPayerForeignResident && ' (תושב חו"ל)'}
                  </span>
                ) : (
                  <EmptyValue>לא הוזן</EmptyValue>
                )}
              </FieldValue>
            </FourColumnInfoField>
          </FourColumnGrid>
        </CollapsibleSection>
      </Section>

      <SectionDivider />

      {/* Case Management */}
      <Section>
        <CollapsibleSection title="ניהול תיק" defaultOpen={true}>
          <ThreeColumnGrid>
            <InfoField>
              <FieldLabel>
                <LabelIcon $color="#8b5cf6"><UserCheck size={16} strokeWidth={2} /></LabelIcon>
                מנהל תיק
              </FieldLabel>
              <FieldValue>
                {client.assignedCaseManagerName || <EmptyValue>לא מוקצה</EmptyValue>}
              </FieldValue>
            </InfoField>

            <InfoField>
              <FieldLabel>
                <LabelIcon $color="#d97706"><Briefcase size={16} strokeWidth={2} /></LabelIcon>
                סטטוס נוכחי
              </FieldLabel>
              {client.currentStatusName && client.currentStatusColor ? (
                <StatusBadge color={client.currentStatusColor}>
                  {client.currentStatusName}
                </StatusBadge>
              ) : (
                <FieldValue><EmptyValue>לא הוגדר</EmptyValue></FieldValue>
              )}
            </InfoField>

            <InfoField>
              <FieldLabel>
                <LabelIcon $color="#64748b"><Clock size={16} strokeWidth={2} /></LabelIcon>
                שינוי סטטוס אחרון
              </FieldLabel>
              <FieldValue>
                {client.lastStatusChangeAt
                  ? formatDateTime(client.lastStatusChangeAt)
                  : <EmptyValue>-</EmptyValue>}
              </FieldValue>
            </InfoField>
          </ThreeColumnGrid>

          <TwoColumnGrid style={{ marginTop: '16px' }}>
            <InfoField>
              <FieldLabel>
                <LabelIcon $color="#0ea5e9"><Calendar size={16} strokeWidth={2} /></LabelIcon>
                תחילת טיפול
              </FieldLabel>
              <FieldValue>
                {client.treatmentStartDate
                  ? formatDateOnly(client.treatmentStartDate)
                  : <EmptyValue>-</EmptyValue>}
              </FieldValue>
            </InfoField>

            <InfoField>
              <FieldLabel>
                <LabelIcon $color="#14b8a6"><Clock size={16} strokeWidth={2} /></LabelIcon>
                ימים בטיפול
              </FieldLabel>
              <FieldValue>
                {client.daysInTreatment > 0
                  ? `${client.daysInTreatment} ימים`
                  : <EmptyValue>-</EmptyValue>}
              </FieldValue>
            </InfoField>
          </TwoColumnGrid>
        </CollapsibleSection>
      </Section>

      <SectionDivider />

      {/* Contract Information */}
      <Section>
        <CollapsibleSection title="פרטי חוזה" defaultOpen={false}>
          <FourColumnGrid>
            <FourColumnInfoField>
              <FieldLabel>
                <LabelIcon $color="#16a34a"><UserCheck size={16} strokeWidth={2} /></LabelIcon>
                נציג מכירות
              </FieldLabel>
              <FieldValue>
                {client.salesRepName || <EmptyValue>-</EmptyValue>}
              </FieldValue>
            </FourColumnInfoField>

            <FourColumnInfoField>
              <FieldLabel>
                <LabelIcon $color="#f59e0b"><Tag size={16} strokeWidth={2} /></LabelIcon>
                מקור
              </FieldLabel>
              <FieldValue>
                {client.leadSourceName || <EmptyValue>-</EmptyValue>}
              </FieldValue>
            </FourColumnInfoField>

            <FourColumnInfoField>
              <FieldLabel>
                <LabelIcon $color="#0ea5e9"><Calendar size={16} strokeWidth={2} /></LabelIcon>
                תאריך חתימה
              </FieldLabel>
              <FieldValue>
                {client.contractSignedDate
                  ? formatDateTime(client.contractSignedDate)
                  : <EmptyValue>-</EmptyValue>}
              </FieldValue>
            </FourColumnInfoField>

            <FourColumnInfoField>
              <FieldLabel>
                <LabelIcon $color="#8b5cf6"><User size={16} strokeWidth={2} /></LabelIcon>
                נוצר על ידי
              </FieldLabel>
              <FieldValue>{client.createdByName}</FieldValue>
            </FourColumnInfoField>

            <FourColumnInfoField>
              <FieldLabel>
                <LabelIcon $color="#64748b"><Calendar size={16} strokeWidth={2} /></LabelIcon>
                תאריך יצירה
              </FieldLabel>
              <FieldValue>{formatDateTime(client.createdAt)}</FieldValue>
            </FourColumnInfoField>
          </FourColumnGrid>
        </CollapsibleSection>
      </Section>

      {/* National Insurance Credentials */}
      <SectionDivider />
      <Section>
        <CollapsibleSection title="גישה לפורטל ביטוח לאומי" defaultOpen={false}>
          <ThreeColumnGrid>
            <InfoField>
              <FieldLabel>
                <LabelIcon $color="#d97706"><User size={16} strokeWidth={2} /></LabelIcon>
                שם משתמש
              </FieldLabel>
              <FieldValue>{client.nationalInsuranceUsername || <EmptyValue>לא הוגדר</EmptyValue>}</FieldValue>
            </InfoField>

            <InfoField>
              <FieldLabel>
                <LabelIcon $color="#dc2626"><Key size={16} strokeWidth={2} /></LabelIcon>
                סיסמה
              </FieldLabel>
              {client.hasNationalInsurancePassword ? (
                <PasswordWrapper>
                  <PasswordMask>••••••••</PasswordMask>
                  <CopyButton
                    onClick={handleCopyPassword}
                    disabled={copyingPassword}
                    $success={copySuccess}
                  >
                    {copyingPassword ? (
                      'מעתיק...'
                    ) : copySuccess ? (
                      <>
                        <Check size={14} strokeWidth={2} />
                        הועתק
                      </>
                    ) : (
                      <>
                        <Copy size={14} strokeWidth={2} />
                        העתק
                      </>
                    )}
                  </CopyButton>
                </PasswordWrapper>
              ) : (
                <FieldValue><EmptyValue>לא הוגדרה סיסמה</EmptyValue></FieldValue>
              )}
            </InfoField>

            {client.lastNationalInsuranceCheck && (
              <InfoField>
                <FieldLabel>
                  <LabelIcon $color="#64748b"><Clock size={16} strokeWidth={2} /></LabelIcon>
                  בדיקה אחרונה
                </FieldLabel>
                <FieldValue>{formatDateTime(client.lastNationalInsuranceCheck)}</FieldValue>
              </InfoField>
            )}
          </ThreeColumnGrid>
        </CollapsibleSection>
      </Section>

      <SectionDivider />

      {/* Clinical Questions */}
      <Section>
        <CollapsibleSection title="היסטוריה רפואית כללית" defaultOpen={false}>
          <QuestionsRow>
            <QuestionItem>
              <QuestionLabel>אשפוז בשנה האחרונה:</QuestionLabel>
              <YesNoIndicator $isYes={client.wasHospitalizedLastYear}>
                {client.wasHospitalizedLastYear ? (
                  <><Check size={18} strokeWidth={2} /> כן</>
                ) : (
                  'לא'
                )}
              </YesNoIndicator>
            </QuestionItem>

            <QuestionItem>
              <QuestionLabel>ניתוח בשנה האחרונה:</QuestionLabel>
              <YesNoIndicator $isYes={client.hadSurgeryLastYear}>
                {client.hadSurgeryLastYear ? (
                  <><Check size={18} strokeWidth={2} /> כן</>
                ) : (
                  'לא'
                )}
              </YesNoIndicator>
            </QuestionItem>

            <QuestionItem>
              <QuestionLabel>תביעה קודמת:</QuestionLabel>
              <YesNoIndicator $isYes={client.submittedClaimBefore}>
                {client.submittedClaimBefore ? (
                  <><Check size={18} strokeWidth={2} /> כן</>
                ) : (
                  'לא'
                )}
              </YesNoIndicator>
            </QuestionItem>
          </QuestionsRow>
        </CollapsibleSection>
      </Section>

      <SectionDivider />

      {/* Medical Conditions */}
      <Section>
        <CollapsibleSection title="תנאים רפואיים / מחלות" defaultOpen={false}>
          <MedicalConditionsSection
            clientId={client.id}
            formatDate={formatDateOnly}
          />
        </CollapsibleSection>
      </Section>
    </Container>
  );
};

export default ClientInfoTab;
